import { ReceiptText, Wallet } from "lucide-react";
import { useRoleStore } from "@/shared/store/role";
import { useBloggerPayouts } from "@/shared/api/wb";
import { fmtRubFull } from "@/shared/lib/format";
import { Skeleton } from "@/shared/ui";

function fmtDateTime(iso: string): { date: string; time: string } {
  const d = new Date(iso);
  const date = d.toLocaleDateString("ru-RU", { day: "2-digit", month: "long", year: "numeric" });
  const time = d.toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
  return { date, time };
}

export function BloggerBalancePage() {
  const activeBloggerId = useRoleStore((s) => s.activeBloggerId);
  const { data, isLoading } = useBloggerPayouts(activeBloggerId);

  if (!activeBloggerId) {
    return (
      <div className="max-w-[640px] mx-auto px-6 py-16 text-center text-ink-40">
        Выберите блогера в шапке, чтобы увидеть баланс.
      </div>
    );
  }

  return (
    <div className="max-w-[800px] mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="font-display font-extrabold text-[28px] tracking-tight">Баланс и выплаты</h1>
        <p className="text-ink-40 text-[13.5px] mt-1">Начисления за подтверждённые рекламодателем размещения</p>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-8">
        <StatCard
          icon={<Wallet size={16} className="text-ink-60" />}
          label="Текущий баланс"
          value={isLoading ? null : fmtRubFull(data?.balance_rub ?? 0)}
        />
        <StatCard
          icon={<ReceiptText size={16} className="text-ink-60" />}
          label="Выплат получено"
          value={isLoading ? null : String(data?.payouts.length ?? 0)}
        />
      </div>

      <div className="bg-white rounded-xl border border-ink-12 overflow-hidden">
        <div className="px-5 py-4 border-b border-ink-12">
          <span className="font-display font-bold text-[15px]">История выплат</span>
        </div>

        {isLoading ? (
          <div className="p-5 flex flex-col gap-3">
            {[1, 2, 3].map((i) => <Skeleton key={i} className="h-14 rounded-lg" />)}
          </div>
        ) : !data?.payouts.length ? (
          <div className="py-16 text-center">
            <Wallet size={32} strokeWidth={1.5} className="text-ink-12 mx-auto mb-3" />
            <div className="font-display font-semibold text-ink-60">Выплат пока нет</div>
            <div className="text-ink-40 text-[12.5px] mt-1">Появятся, когда рекламодатель подтвердит отчёт по размещению</div>
          </div>
        ) : (
          <table className="w-full text-[13px]">
            <thead>
              <tr className="text-[10.5px] uppercase tracking-wider text-ink-40 border-b border-ink-12">
                {["Дата", "За что", "Сумма", "Баланс после"].map((h, i) => (
                  <th key={i} className="text-left px-5 py-2.5 font-display font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.payouts.map((tx) => {
                const { date, time } = fmtDateTime(tx.created_at);
                return (
                  <tr key={tx.id} className="border-b border-ink-12/50 last:border-0 hover:bg-ink-6/50">
                    <td className="px-5 py-3.5">
                      <div className="font-display font-semibold">{date}</div>
                      <div className="text-[11px] text-ink-40 font-mono">{time}</div>
                    </td>
                    <td className="px-5 py-3.5 text-ink-60">{tx.campaign_product || "—"}</td>
                    <td className="px-5 py-3.5">
                      <span className="font-mono font-bold text-success text-[14px]">+{fmtRubFull(tx.amount)}</span>
                    </td>
                    <td className="px-5 py-3.5 font-mono text-ink-40">{fmtRubFull(tx.balance_after)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string | null }) {
  return (
    <div className="bg-white rounded-xl border border-ink-12 px-5 py-4">
      <div className="flex items-center gap-2 mb-3 text-ink-40">
        {icon}
        <span className="text-[11px] font-display font-semibold uppercase tracking-wider">{label}</span>
      </div>
      {value === null ? (
        <Skeleton className="h-8 w-32 rounded" />
      ) : (
        <div className="font-display font-extrabold text-[22px] tracking-tight tabular-nums">{value}</div>
      )}
    </div>
  );
}
